import ProductDeviceActivation from "./ProductDeviceActivation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowUpRight, CreditCard, Download, ShieldCheck } from "lucide-react";
import { useSecureMacProAccount } from "@/context/SecureMacProAccountContext";
import {
  consumerErrorMessage,
  downloadSecureAIRelease,
  fetchConsumerCatalog,
  openStripeBillingPortal,
  startSecureAICheckout,
} from "@/lib/consumerCommerce";

const PURCHASE_CONFIRM_ATTEMPTS = 10;
const PURCHASE_CONFIRM_INTERVAL_MS = 2000;

function formatPrice(amount, currency) {
  if (!Number.isInteger(amount) || !currency) return "";
  if (currency.toLowerCase() === "aud") return `A$${(amount / 100).toFixed(2)}`;
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

function statusLabel(entitlement) {
  if (entitlement?.active) return "Subscribed · Active";
  if (entitlement?.status === "revoked") return "Cancelled or reversed";
  if (entitlement?.status === "expired") return "Subscription expired";
  return "Not subscribed yet";
}

export default function SecureAIAccountPanel({ purchaseState = "" }) {
  const {
    account,
    secureAIEntitlement,
    secureAIEntitlementError,
    secureAIDevices,
    secureAIDevicesError,
    purchases,
    purchasesError,
    releaseSecureAIDevice,
    refresh,
  } = useSecureMacProAccount();
  const [product, setProduct] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [confirmationAttempts, setConfirmationAttempts] = useState(0);

  useEffect(() => {
    let active = true;
    fetchConsumerCatalog()
      .then((catalog) => {
        if (!active) return;
        setProduct((catalog?.products || []).find((item) => item.key === "secureai") || null);
        setCatalogError("");
      })
      .catch(() => {
        if (!active) return;
        setProduct(null);
        setCatalogError("unavailable");
      });
    return () => {
      active = false;
    };
  }, []);

  const active = secureAIEntitlement?.active === true;
  const purchasable = product?.enabled === true && product?.verification_status === "ready";
  const secureAIPurchases = useMemo(
    () => (purchases || []).filter((purchase) => purchase.product === "secureai"),
    [purchases],
  );

  useEffect(() => {
    if (
      purchaseState !== "success"
      || active
      || confirmationAttempts >= PURCHASE_CONFIRM_ATTEMPTS
    ) {
      return undefined;
    }
    const timer = setTimeout(async () => {
      await refresh();
      setConfirmationAttempts((value) => value + 1);
    }, PURCHASE_CONFIRM_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [purchaseState, active, confirmationAttempts, refresh]);

  const buy = useCallback(async () => {
    setBusy("buy");
    setError("");
    try {
      const { url } = await startSecureAICheckout();
      window.location.assign(url);
    } catch (err) {
      setError(consumerErrorMessage(err, "SecureAI checkout couldn't start. You haven't been charged."));
    } finally {
      setBusy("");
    }
  }, []);

  const download = useCallback(async () => {
    setBusy("download");
    setError("");
    try {
      const { download_url: url } = await downloadSecureAIRelease();
      window.location.assign(url);
    } catch (err) {
      setError(consumerErrorMessage(err, "Your SecureAI download isn't ready yet. Your subscription is safe."));
    } finally {
      setBusy("");
    }
  }, []);

  const manageBilling = useCallback(async () => {
    setBusy("billing");
    setError("");
    try {
      const { url } = await openStripeBillingPortal();
      window.location.assign(url);
    } catch (err) {
      setError(consumerErrorMessage(err, "Billing management couldn't open. Please try again."));
    } finally {
      setBusy("");
    }
  }, []);

  const release = useCallback(
    async (device) => {
      if (!window.confirm("Release this Mac from your SecureAI subscription?")) return;
      setBusy(device.id);
      setError("");
      try {
        await releaseSecureAIDevice(device.id);
      } catch (err) {
        setError(consumerErrorMessage(err, "This Mac couldn't be released. Please try again."));
      } finally {
        setBusy("");
      }
    },
    [releaseSecureAIDevice],
  );

  if (!account) return null;

  return (
    <section
      className="mx-auto max-w-2xl border border-ink-100 bg-white p-7 md:p-9"
      aria-labelledby="secureai-account-heading"
      data-testid="secureai-account"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <ShieldCheck className="h-6 w-6 text-electric" strokeWidth={1.7} />
          <div className="lf-eyebrow mt-4">SecureAI subscription</div>
          <h2 id="secureai-account-heading" className="mt-2 font-display text-2xl font-bold">
            SecureAI
          </h2>
          <p className="mt-1 text-sm text-ink-500">{account.email}</p>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[.16em] text-ink-500">
          {statusLabel(secureAIEntitlement)}
        </span>
      </div>

      {purchaseState === "cancelled" ? (
        <p className="mt-6 border border-ink-100 bg-ink-50 p-4 text-sm text-ink-700">
          Checkout was cancelled. You haven't been charged.
        </p>
      ) : purchaseState === "success" && active ? (
        <p className="mt-6 border border-green-600/30 bg-green-50 p-4 text-sm text-green-800">
          You're all set. SecureAI is ready to download.
        </p>
      ) : purchaseState === "success" && confirmationAttempts < PURCHASE_CONFIRM_ATTEMPTS ? (
        <p className="mt-6 border border-electric/30 bg-electric/5 p-4 text-sm text-ink-700" role="status">
          Payment received — confirming your subscription. This usually takes a few seconds.
        </p>
      ) : purchaseState === "success" ? (
        <p className="mt-6 border border-amber-500/40 bg-amber-50 p-4 text-sm text-ink-700" role="status">
          Your payment is still being confirmed. Refresh this page in a minute; you won't be charged twice.
        </p>
      ) : null}

      {secureAIEntitlementError ? (
        <p className="mt-6 text-sm text-red-600" role="alert">
          SecureAI subscription status is temporarily unavailable. No purchase or download action is enabled.
        </p>
      ) : (
        <div className="mt-6 flex flex-wrap gap-3">
          {active ? (
            <>
              {product?.delivery_enabled === true ? (
                <button
                  className="lf-btn-primary"
                  type="button"
                  onClick={download}
                  disabled={Boolean(busy)}
                  data-testid="secureai-download"
                >
                  <Download className="h-4 w-4" />
                  {busy === "download" ? "Preparing…" : "Download SecureAI"}
                </button>
              ) : (
                <span className="text-sm text-ink-500" data-testid="secureai-download-held">
                  Download temporarily unavailable while the approved release is prepared.
                </span>
              )}
              <button
                className="lf-btn-secondary"
                type="button"
                onClick={manageBilling}
                disabled={Boolean(busy)}
                data-testid="secureai-manage-billing"
              >
                <CreditCard className="h-4 w-4" />
                {busy === "billing" ? "Opening…" : "Manage Stripe billing"}
              </button>
            </>
          ) : purchasable ? (
            <button
              className="lf-btn-primary"
              type="button"
              onClick={buy}
              disabled={Boolean(busy)}
              data-testid="secureai-buy"
            >
              {busy === "buy"
                ? "Starting secure checkout…"
                : `Subscribe · ${formatPrice(product.unit_amount, product.currency)} / month`}
              <ArrowUpRight className="h-4 w-4" />
            </button>
          ) : (
            <p className="text-sm text-ink-500" data-testid="secureai-not-on-sale">
              {catalogError
                ? "We couldn't load SecureAI pricing. Refresh to try again."
                : "SecureAI isn't on sale yet."}
            </p>
          )}
        </div>
      )}
      {error ? <p className="mt-3 text-sm text-red-600" role="alert">{error}</p> : null}
      {active && secureAIEntitlement?.expires_at ? (
        <p className="mt-3 text-sm text-ink-600">
          Paid through {new Date(secureAIEntitlement.expires_at).toLocaleDateString()}.
        </p>
      ) : null}
      <p className="mt-3 text-xs text-ink-500">
        Stripe manages this website subscription. App Store purchases remain managed by their originating store.
      </p>

      {active ? (
        <section className="mt-6 border-t border-ink-100 pt-5" data-testid="secureai-devices">
          <h3 className="font-display text-lg font-bold">Authorised Macs</h3>
          {secureAIDevicesError ? (
            <p className="mt-2 text-sm text-red-600">Device status is temporarily unavailable.</p>
          ) : secureAIDevices.length ? (
            <div className="mt-3 divide-y divide-ink-100">
              {secureAIDevices.map((device, index) => (
                <div key={device.id} className="flex items-center justify-between gap-4 py-3 text-sm">
                  <div>
                    <div className="font-semibold text-ink-900">Mac {index + 1}</div>
                    <div className="text-ink-500">
                      Active · Last used {new Date(device.last_used_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    className="lf-btn-secondary"
                    type="button"
                    disabled={Boolean(busy)}
                    onClick={() => release(device)}
                    data-testid="secureai-release-device"
                  >
                    {busy === device.id ? "Releasing…" : "Release"}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-sm text-ink-500">No Macs are authorised yet.</p>
          )}
          <p className="mt-2 text-xs text-ink-500">
            Up to 3 active Macs. Release one before authorising a replacement.
          </p>
        </section>
      ) : null}

      {active && !secureAIEntitlementError ? <div className="mt-6"><ProductDeviceActivation product="secureai" productName="SecureAI" devices={secureAIDevices} devicesError={secureAIDevicesError} /></div> : null}
      {purchasesError ? (
        <p className="mt-6 text-sm text-red-600">SecureAI purchase history is temporarily unavailable.</p>
      ) : secureAIPurchases.length ? (
        <section className="mt-6 border-t border-ink-100 pt-5" data-testid="secureai-purchase-history">
          <h3 className="font-display text-lg font-bold">Subscription history</h3>
          <div className="mt-3 space-y-3">
            {secureAIPurchases.map((purchase, index) => (
              <div
                key={`${purchase.purchased_at || "period"}-${index}`}
                className="flex flex-wrap justify-between gap-3 text-sm"
              >
                <div>
                  <div className="font-semibold text-ink-900">SecureAI</div>
                  <div className="text-ink-500">Website subscription · Stripe</div>
                </div>
                <div className="text-right text-ink-600">
                  <div>{formatPrice(purchase.amount_total, purchase.currency)}</div>
                  <div className="capitalize">{String(purchase.status || "").replaceAll("_", " ")}</div>
                  <div>
                    {purchase.purchased_at
                      ? new Date(purchase.purchased_at).toLocaleDateString()
                      : "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </section>
  );
}
